import { useState, useEffect, useRef } from "react";
import { useLang } from "../contexts/LanguageContext";
import { PublicLangToggle } from "../components/PublicLangToggle";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Sparkle, Upload, Brain, ChatCircle, ThumbsUp, Robot, MagicWand, Envelope,
  ArrowRight, ArrowLeft, Play, Pause, X, CheckCircle, Lightning,
} from "@phosphor-icons/react";

// Tâche 1396 — Les deux langues sur la même entrée (motif 1324b) : cette
// table vit hors composant, un appel au traducteur y serait hors du hook.
// Coquilles corrigées au passage : « identifié les lacunes » → « identifie »,
// « identifiér des thèmes » → « identifier ».
const SLIDES = [
  {
    id: "intro",
    duration: 8,
    icon: Sparkle,
    color: "from-cyan-400 to-blue-600",
    chip: "Bienvenue",
    chip_en: "Welcome",
    title: "KNORIA en 3 minutes",
    title_en: "KNORIA in 3 minutes",
    subtitle: "Une démonstration des 7 fonctionnalités-phares",
    subtitle_en: "A walkthrough of the 7 flagship features",
    description: "Cette plateforme transforme vos vidéos de formation en un assistant IA qui répond à vos conseillers 24/7, identifie les lacunes et s'auto-améliore.",
    description_en: "This platform turns your training videos into an AI assistant that answers your advisors around the clock, spots the gaps and improves itself.",
    bullets: [
      "Pipeline IA ancré dans VOS contenus (zéro invention)",
      "Conformité Loi 25 — 100 % au Canada (OVHcloud Beauharnois)",
      "Cloisonnement strict par organisation",
    ],
    bullets_en: [
      "AI pipeline grounded in YOUR content (nothing invented)",
      "Law 25 compliant — 100% in Canada (OVHcloud Beauharnois)",
      "Strict separation between organisations",
    ],
    cta: null,
  },
  {
    id: "upload",
    duration: 22,
    icon: Upload,
    color: "from-blue-500 to-indigo-700",
    chip: "Étape 1 / 7",
    chip_en: "Step 1 / 7",
    title: "Téléversement de vidéos",
    title_en: "Uploading videos",
    subtitle: "Glissez-déposez. Le pipeline IA fait le reste.",
    subtitle_en: "Drag and drop. The AI pipeline does the rest.",
    description: "Téléversez vos formations existantes (Zoom, Teams, captures d'écran). Le pipeline extrait tout seul : transcription, locuteurs identifiés, découpage par thème, indexation vectorielle.",
    description_en: "Upload the training you already have (Zoom, Teams, screen recordings). The pipeline extracts everything on its own: transcript, identified speakers, topic segmentation, vector indexing.",
    bullets: [
      "Whisper + AssemblyAI pour la transcription et la diarisation",
      "Embeddings rangés dans la base vectorielle MongoDB",
      "Statut en temps réel (en attente → traitement → terminé)",
    ],
    bullets_en: [
      "Whisper + AssemblyAI for transcription and diarisation",
      "Embeddings stored in the MongoDB vector database",
      "Live status (pending → processing → done)",
    ],
    cta: { label: "Voir l'Explorateur", label_en: "Open the Explorer", path: "/dashboard/explorer" },
  },
  {
    id: "rag",
    duration: 28,
    icon: ChatCircle,
    color: "from-violet-500 to-purple-700",
    chip: "Étape 2 / 7",
    chip_en: "Step 2 / 7",
    title: "Dialogueur RAG sans invention",
    title_en: "RAG chatbot that invents nothing",
    subtitle: "Vos conseillers posent des questions en langage naturel.",
    subtitle_en: "Your advisors ask questions in plain language.",
    description: "Le dialogueur ne fabrique RIEN. Il cite uniquement ce qui est dans vos transcriptions, avec des repères de temps cliquables qui ouvrent la vidéo au moment exact.",
    description_en: "The chatbot fabricates NOTHING. It quotes only what is in your transcripts, with clickable timestamps that open the video at the exact moment.",
    bullets: [
      "Sources cliquables avec repères de temps précis",
      "Refus explicite quand l'information est absente",
      "Format Vincent strict : structuré, professionnel, vouvoiement québécois",
    ],
    bullets_en: [
      "Clickable sources with precise timestamps",
      "Says so plainly when the information is not there",
      "Strict Vincent format: structured, professional, Quebec formal address",
    ],
    cta: { label: "Essayer le clavardage", label_en: "Try the chat", path: "/dashboard/chat" },
  },
  {
    id: "feedback",
    duration: 18,
    icon: ThumbsUp,
    color: "from-teal-500 to-teal-700",
    chip: "Étape 3 / 7",
    chip_en: "Step 3 / 7",
    title: "Retours en 4 catégories",
    title_en: "Feedback in 4 categories",
    subtitle: "Chaque réponse peut être notée par les conseillers.",
    subtitle_en: "Advisors can rate every answer.",
    description: "Bonne réponse · Pas clair · Hors sujet · Lien non fonctionnel. Les signaux négatifs alimentent la boucle d'auto-amélioration.",
    description_en: "Good answer · Unclear · Off topic · Broken link. The negative signals feed the self-improvement loop.",
    bullets: [
      "Statistiques par organisation (satisfaction en %, par catégorie)",
      "Commentaires libres pour préciser le problème",
      "Déclenchement automatique d'une Q&R dès 3 retours négatifs sur la même question",
    ],
    bullets_en: [
      "Per-organisation statistics (satisfaction %, by category)",
      "Free-text comments to pin down the problem",
      "A Q&A is triggered automatically after 3 negative ratings on the same question",
    ],
    cta: null,
  },
  {
    id: "agent",
    duration: 28,
    icon: Robot,
    color: "from-amber-500 to-orange-700",
    chip: "Étape 4 / 7",
    chip_en: "Step 4 / 7",
    title: "Agent Knoria autonome",
    title_en: "The Knoria Agent, on its own",
    subtitle: "L'Agent surveille en permanence la santé de votre base.",
    subtitle_en: "The Agent keeps watch over the health of your knowledge base.",
    description: "Score de santé sur 100, principales lacunes détectées, satisfaction du dialogueur. L'Agent propose de lui-même des actions : générer des Q&R, téléverser une vidéo, identifier des thèmes manquants.",
    description_en: "Health score out of 100, the main gaps found, chatbot satisfaction. The Agent suggests what to do next on its own: generate Q&A, upload a video, identify missing topics.",
    bullets: [
      "Encadré « Questions mal notées » en temps réel",
      "Cycle d'analyse manuel ou automatique",
      "Conversations en langage naturel avec l'Agent",
    ],
    bullets_en: [
      "Live “Poorly rated questions” panel",
      "Analysis cycle, manual or automatic",
      "Plain-language conversations with the Agent",
    ],
    cta: { label: "Ouvrir l'Agent", label_en: "Open the Agent", path: "/dashboard/agent" },
  },
  {
    id: "auto_qa",
    duration: 30,
    icon: MagicWand,
    color: "from-pink-500 to-rose-700",
    chip: "Étape 5 / 7",
    chip_en: "Step 5 / 7",
    title: "Q&R générées automatiquement (la qualité d'abord)",
    title_en: "Q&A generated automatically (quality first)",
    subtitle: "Chaîne de 4 consignes, selon la spécification de Vincent.",
    subtitle_en: "A chain of 4 prompts, following Vincent's specification.",
    description: "L'Agent ne retient QUE les passages de qualité — il laisse de côté les transitions, les salutations et ce qui se répète. Chaque Q&R retenue suit les 9 colonnes du fichier maître de Vincent : thème, mots-clés, question, réponse, repère de temps, type de question, citation textuelle, catégorie, sous-catégorie.",
    description_en: "The Agent keeps ONLY the passages worth keeping — it leaves out transitions, greetings and anything repeated. Each Q&A follows the 9 columns of Vincent's master file: theme, keywords, question, answer, timestamp, question type, verbatim quote, category, sub-category.",
    bullets: [
      "Garde-fous : un passage anodin ou trop court est écarté tout seul",
      "Anti-doublon : refus si la question ressemble trop à une Q&R existante",
      "Export CSV de 18 colonnes, compatible Excel (format maître de Vincent)",
    ],
    bullets_en: [
      "Guardrails: a passage that is trivial or too short is dropped on its own",
      "No duplicates: refused if the question is too close to an existing Q&A",
      "18-column CSV export, Excel-friendly (Vincent's master format)",
    ],
    cta: null,
  },
  {
    id: "validation",
    duration: 22,
    icon: Lightning,
    color: "from-fuchsia-500 to-violet-700",
    chip: "Étape 6 / 7",
    chip_en: "Step 6 / 7",
    title: "Validation rapide, une carte à la fois",
    title_en: "Quick review, one card at a time",
    subtitle: "Balayez à gauche pour rejeter, à droite pour approuver.",
    subtitle_en: "Swipe left to reject, right to approve.",
    description: "Une pile de cartes animée, les raccourcis ←/→/E pour éditer, et la modification sur place avec regénération de l'embedding. Vincent valide 20 brouillons en 2 minutes pendant son café.",
    description_en: "An animated card stack, ←/→/E shortcuts to edit, and in-place editing with the embedding regenerated. Vincent reviews 20 drafts in 2 minutes over his coffee.",
    bullets: [
      "Repère de temps cliquable sur chaque carte",
      "Indice de confiance visible (vert ≥ 80 %, ambre ≥ 60 %)",
      "Compteur en direct (approuvés / rejetés / restants)",
    ],
    bullets_en: [
      "Clickable timestamp on every card",
      "Confidence score visible (green ≥ 80%, amber ≥ 60%)",
      "Live counter (approved / rejected / left)",
    ],
    cta: { label: "Voir la file", label_en: "Open the queue", path: "/dashboard/agent/drafts" },
  },
  {
    id: "score",
    duration: 22,
    icon: Envelope,
    color: "from-indigo-500 to-blue-800",
    chip: "Étape 7 / 7",
    chip_en: "Step 7 / 7",
    title: "Score Knoria hebdomadaire",
    title_en: "Weekly Knoria score",
    subtitle: "Un courriel de synthèse soigné, aux fondateurs et aux admins.",
    subtitle_en: "A polished summary email, to the founders and admins.",
    description: "Chaque lundi, un bilan visuel : score sur 100, écart avec la semaine précédente, satisfaction du dialogueur, les 3 principales lacunes qui restent, les brouillons en attente.",
    description_en: "Every Monday, a visual round-up: score out of 100, change since last week, chatbot satisfaction, the top 3 gaps still open, the drafts waiting.",
    bullets: [
      "Gabarit HTML soigné (dégradé KNORIA bleu et blanc)",
      "Recommandations de l'Agent et bouton de validation",
      "Un score qui suit l'usage réel de la plateforme",
    ],
    bullets_en: [
      "Polished HTML template (KNORIA blue and white gradient)",
      "Agent recommendations and a review button",
      "A score that follows how the platform is really used",
    ],
    cta: null,
  },
  {
    id: "outro",
    duration: 14,
    icon: CheckCircle,
    color: "from-teal-500 to-cyan-600",
    chip: "Conclusion",
    chip_en: "Wrap-up",
    title: "La boucle complète d'auto-amélioration",
    title_en: "The full self-improvement loop",
    subtitle: "Une plateforme vivante, qui s'enrichit toute seule.",
    subtitle_en: "A living platform that enriches itself.",
    description: "Un conseiller signale 👎 → l'Agent détecte → une Q&R est générée → Vincent la valide → l'embedding est refait → le dialogueur s'améliore → le courriel part aux fondateurs.",
    description_en: "An advisor flags 👎 → the Agent spots it → a Q&A is generated → Vincent reviews it → the embedding is rebuilt → the chatbot improves → the email goes out to the founders.",
    bullets: [
      "9 livrables en plus des engagements de départ",
      "Chaque vidéo couverte en entier, sans le contenu inutile",
      "Prête pour Finance Libre HQ et les clients d'entreprise",
    ],
    bullets_en: [
      "9 deliverables beyond what was originally promised",
      "Every video covered in full, without the filler",
      "Ready for Finance Libre HQ and enterprise customers",
    ],
    cta: { label: "Démarrer un essai", label_en: "Start a trial", path: "/dashboard" },
  },
];


export default function DemoTourPage() {
  // Tâche 1396 — `L` lit la version anglaise quand elle existe, sinon la
  // française : une entrée oubliée reste lisible au lieu de disparaître.
  const { tr, lang } = useLang();
  const L = (o, cle) => (lang === "en" && o?.[`${cle}_en`] ? o[`${cle}_en`] : o?.[cle]);
  const navigate = useNavigate();
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef(null);

  const slide = SLIDES[idx];
  const totalDuration = SLIDES.reduce((sum, s) => sum + s.duration, 0);
  const elapsedBefore = SLIDES.slice(0, idx).reduce((sum, s) => sum + s.duration, 0);

  useEffect(() => {
    setProgress(0);
    if (!playing) return;
    const tick = 100;
    const stepPct = tick / (slide.duration * 1000) * 100;
    intervalRef.current = setInterval(() => {
      setProgress((p) => {
        if (p + stepPct >= 100) {
          if (idx < SLIDES.length - 1) {
            setIdx(idx + 1);
            return 0;
          } else {
            setPlaying(false);
            return 100;
          }
        }
        return p + stepPct;
      });
    }, tick);
    return () => clearInterval(intervalRef.current);
  }, [idx, playing, slide.duration]);

  const Icon = slide.icon;
  const totalElapsed = Math.round(elapsedBefore + (progress / 100) * slide.duration);
  const minutes = Math.floor(totalElapsed / 60);
  const seconds = totalElapsed % 60;
  const totalMin = Math.floor(totalDuration / 60);
  const totalSec = totalDuration % 60;

  return (
    <div className="fixed inset-0 z-[60] bg-zinc-950 text-white overflow-hidden flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-white/10 bg-black/40 backdrop-blur">
        <div className="flex items-center gap-3">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-white/60">KNORIA</div>
          <div className="w-px h-4 bg-white/20" />
          <div className="text-xs text-white/60">{tr("Démonstration interactive", "Interactive walkthrough")}</div>
        </div>
        <div className="flex items-center gap-3">
          <PublicLangToggle tone="dark" />
          <div className="text-xs font-mono text-white/70" data-testid="demo-timer">
            {String(minutes).padStart(2, "0")}:{String(seconds).padStart(2, "0")} / {String(totalMin).padStart(2, "0")}:{String(totalSec).padStart(2, "0")}
          </div>
          <button
            onClick={() => navigate("/dashboard")}
            className="p-1.5 rounded-md hover:bg-white/10 transition"
            data-testid="close-demo"
            title={tr("Fermer la démo", "Close the walkthrough")}
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Progress dots */}
      <div className="flex items-center justify-center gap-1.5 px-6 py-3 bg-black/40">
        {SLIDES.map((s, i) => (
          <button
            key={s.id}
            onClick={() => { setIdx(i); setProgress(0); }}
            className={`h-1.5 rounded-full transition-all ${
              i === idx ? "w-12 bg-white" : i < idx ? "w-6 bg-white/60" : "w-6 bg-white/15"
            }`}
            data-testid={`demo-dot-${i}`}
            aria-label={L(s, "title")}
          />
        ))}
      </div>

      {/* Main slide */}
      <div className="flex-1 flex items-center justify-center p-8 lg:p-16 overflow-hidden">
        <div
          key={slide.id}
          className="max-w-4xl w-full animate-in fade-in slide-in-from-bottom-4 duration-500"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/50">
              {L(slide, "chip")}
            </span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <div className={`inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-gradient-to-br ${slide.color} mb-8 shadow-2xl`}>
            <Icon size={40} weight="duotone" className="text-white" />
          </div>

          <h1 className="text-4xl lg:text-6xl font-bold mb-4 leading-tight tracking-tight" data-testid="demo-title">
            {L(slide, "title")}
          </h1>
          <p className="text-xl lg:text-2xl text-white/70 mb-8 font-light max-w-3xl leading-snug">
            {L(slide, "subtitle")}
          </p>
          <p className="text-base text-white/60 mb-10 max-w-2xl leading-relaxed">
            {L(slide, "description")}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
            {(L(slide, "bullets") || []).map((b, i) => (
              <div
                key={i}
                className="bg-white/5 border border-white/10 rounded-2xl p-5 hover:bg-white/10 transition"
                data-testid={`demo-bullet-${i}`}
              >
                <CheckCircle size={20} weight="fill" className={`bg-gradient-to-br ${slide.color} bg-clip-text mb-3`} style={{ color: "white" }} />
                <p className="text-sm leading-relaxed text-white/80">{b}</p>
              </div>
            ))}
          </div>

          {slide.cta && (
            <Button
              onClick={() => navigate(slide.cta.path)}
              className={`gap-2 bg-gradient-to-r ${slide.color} hover:opacity-90 text-white px-6 py-3 h-auto text-base font-semibold`}
              data-testid="demo-cta-btn"
            >
              {L(slide.cta, "label")}
              <ArrowRight size={16} />
            </Button>
          )}
        </div>
      </div>

      {/* Footer controls */}
      <div className="border-t border-white/10 bg-black/40 backdrop-blur px-6 py-4 flex items-center justify-between gap-4">
        <Button
          onClick={() => { setIdx(Math.max(0, idx - 1)); setProgress(0); }}
          disabled={idx === 0}
          variant="ghost"
          className="gap-2 text-white hover:bg-white/10 disabled:opacity-30"
          data-testid="demo-prev"
        >
          <ArrowLeft size={16} /> {tr("Précédent", "Previous")}
        </Button>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setPlaying(!playing)}
            className="w-10 h-10 rounded-full bg-white text-black flex items-center justify-center hover:bg-white/90 transition shadow-lg"
            data-testid="demo-playpause"
            title={playing ? tr("Pause", "Pause") : tr("Lecture", "Play")}
          >
            {playing ? <Pause size={16} weight="fill" /> : <Play size={16} weight="fill" />}
          </button>
          <div className="text-xs text-white/60 hidden md:block">
            {idx + 1} / {SLIDES.length} · {slide.duration}s
          </div>
        </div>

        <Button
          onClick={() => {
            if (idx < SLIDES.length - 1) {
              setIdx(idx + 1);
              setProgress(0);
            } else {
              navigate("/dashboard");
            }
          }}
          className="gap-2 bg-white text-black hover:bg-white/90"
          data-testid="demo-next"
        >
          {idx < SLIDES.length - 1 ? tr("Suivant", "Next") : tr("Terminer", "Finish")} <ArrowRight size={16} />
        </Button>
      </div>

      {/* Bottom progress bar */}
      <div className="h-1 bg-white/10">
        <div
          className={`h-full bg-gradient-to-r ${slide.color} transition-all`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}
